import type { Lesson } from './courses';

/**
 * Parcours 1 : Growth Marketing 101 — 6 lecons.
 */
export const GROWTH_101_LESSONS: Lesson[] = [
  {
    slug: 'what-is-growth',
    title: "Qu'est-ce que le Growth Marketing ?",
    duration: '3 min',
    glossaryTerms: ['growth-dashboard', 'kpi'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Le Growth Marketing est une approche data-driven du marketing qui vise a optimiser chaque etape du parcours client. Contrairement au marketing traditionnel centre sur l'acquisition, le growth marketing couvre l'ensemble du cycle de vie : acquisition, activation, retention, recommandation et revenu (framework AARRR).\n\nL'idee centrale : chaque action marketing doit etre mesurable, testable et optimisable.",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Les entreprises SaaS qui adoptent une approche growth marketing affichent en moyenne une croissance 2 a 3 fois superieure a celles qui s'appuient uniquement sur le marketing traditionnel. En se concentrant sur les metriques qui comptent (MRR, churn, LTV), vous pouvez identifier les leviers les plus efficaces et allouer vos ressources de maniere optimale.\n\nPar exemple, ameliorer la retention de seulement 5% peut augmenter les profits de 25 a 95% (Harvard Business Review).",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Definir vos objectifs** — Choisissez 2-3 KPIs prioritaires (ex: MRR, taux de conversion, churn rate)\n2. **Cartographier votre funnel** — Identifiez chaque etape du parcours client et mesurez les taux de conversion\n3. **Formuler des hypotheses** — Pour chaque etape faible, identifiez des leviers d'amelioration\n4. **Tester rapidement** — Lancez des experiences a petite echelle (A/B tests, landing pages)\n5. **Mesurer et iterer** — Analysez les resultats, gardez ce qui marche, abandonnez le reste",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le **Marketing Dashboard** vous donne une vue d'ensemble de vos metriques growth : contenus generes, prospects contactes, taux de conversion du parrainage et MRR estime. C'est votre cockpit pour piloter votre strategie growth au quotidien.",
      },
    ],
    linkedModule: {
      label: 'Marketing Dashboard',
      path: '/growth/marketing-dashboard',
      icon: 'TrendingUp',
    },
  },
  {
    slug: 'aarrr-funnel',
    title: 'Le framework AARRR (Pirate Metrics)',
    duration: '4 min',
    glossaryTerms: ['pipeline', 'kpi'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Le framework AARRR (aussi appele Pirate Metrics, car les initiales forment le cri du pirate) decompose le parcours client en 5 etapes :\n\n- **Acquisition** — Comment les utilisateurs vous decouvrent-ils ?\n- **Activation** — Ont-ils une premiere experience positive ?\n- **Retention** — Reviennent-ils utiliser votre produit ?\n- **Recommandation** — En parlent-ils autour d'eux ?\n- **Revenu** — Generent-ils du chiffre d'affaires ?\n\nCe framework, cree par Dave McClure (500 Startups), est devenu la reference pour les equipes growth.",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Sans une vision claire de votre funnel, vous risquez d'investir massivement en acquisition alors que votre probleme se situe en retention. Le framework AARRR vous permet d'identifier precisement ou se situe votre plus gros levier de croissance.\n\nStatistique cle : acquérir un nouveau client coute 5 a 25 fois plus cher que de fideliser un client existant.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Mesurez chaque etape** — Definissez un KPI pour chacune des 5 etapes\n2. **Identifiez le goulot d'etranglement** — Quelle etape a le plus mauvais taux de conversion ?\n3. **Concentrez vos efforts** — Travaillez sur le maillon le plus faible en priorite\n4. **Testez et mesurez** — Chaque amelioration doit etre quantifiable\n5. **Recommencez** — Le funnel est un cycle d'amelioration continue",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le **Marketing Dashboard** structure vos metriques selon ce framework : combien de contenus generent de l'acquisition (Content Factory), combien de prospects progressent dans le pipeline (Sales Intelligence), et quel est votre taux de parrainage (Referral Network).",
      },
    ],
    linkedModule: {
      label: 'Marketing Dashboard',
      path: '/growth/marketing-dashboard',
      icon: 'TrendingUp',
    },
  },
  {
    slug: 'kpis-metrics',
    title: 'KPIs essentiels du Growth',
    duration: '4 min',
    glossaryTerms: ['mrr', 'arr', 'churn', 'kpi'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Les KPIs (Key Performance Indicators) sont les metriques qui mesurent la sante et la croissance de votre business. Pour un SaaS, les plus importants sont :\n\n- **MRR** (Monthly Recurring Revenue) — Revenu mensuel recurrent\n- **ARR** (Annual Recurring Revenue) — MRR x 12\n- **Churn Rate** — Taux d'attrition (% de clients perdus par mois)\n- **LTV** (Lifetime Value) — Valeur vie client (revenu moyen genere par client)\n- **CAC** (Customer Acquisition Cost) — Cout d'acquisition d'un client\n- **Ratio LTV/CAC** — Idealement superieur a 3",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Un SaaS sain a un ratio LTV/CAC superieur a 3 et un churn mensuel inferieur a 5%. Ces metriques vous permettent de savoir si votre modele est viable et ou concentrer vos efforts.\n\nExemple concret : si votre CAC est de 200 EUR et votre LTV de 400 EUR (ratio de 2), vous etes en danger. Il faut soit reduire le CAC, soit augmenter la retention pour ameliorer la LTV.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Calculez votre MRR** — Somme de tous les abonnements actifs\n2. **Mesurez votre churn** — Nombre de desabonnements / nombre total de clients\n3. **Estimez votre LTV** — MRR moyen par client / taux de churn mensuel\n4. **Calculez votre CAC** — Budget marketing + sales / nombre de nouveaux clients\n5. **Suivez le ratio LTV/CAC** — Objectif : superieur a 3",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "L'onglet **Abonnements** dans les Operations affiche votre MRR, ARR et le detail des abonnements Stripe. Le **Marketing Dashboard** vous donne une estimation du MRR potentiel genere par vos actions marketing.",
      },
    ],
    linkedModule: {
      label: 'Abonnements',
      path: '/ops/subscriptions',
      icon: 'CreditCard',
    },
  },
  {
    slug: 'growth-loops',
    title: 'Les boucles de croissance (Growth Loops)',
    duration: '3 min',
    glossaryTerms: ['referral-network', 'pipeline'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Un growth loop est un mecanisme auto-renforçant ou chaque utilisateur existant contribue a en attirer de nouveaux. Contrairement au funnel lineaire (acquisition → conversion), une boucle est circulaire :\n\n1. Un utilisateur utilise le produit\n2. Il cree de la valeur visible (contenu, avis, partage)\n3. Cette valeur attire de nouveaux utilisateurs\n4. Les nouveaux utilisateurs repetent le cycle\n\nExemples celebres : Dropbox (parrainage), LinkedIn (invitation contacts), Slack (equipes invitant d'autres equipes).",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Les boucles de croissance generent une croissance composee : plus vous avez d'utilisateurs, plus la boucle s'accelere. Dropbox a atteint 4 millions d'utilisateurs en 15 mois grace a son programme de parrainage (espace de stockage bonus pour chaque invitation acceptee).",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Identifiez votre boucle naturelle** — Quelle action de vos utilisateurs peut en attirer de nouveaux ?\n2. **Reduisez les frictions** — Facilitez le partage, le parrainage, l'invitation\n3. **Creez une incitation** — Offrez une recompense aux parrains et aux filleuls\n4. **Mesurez le K-factor** — Nombre moyen de nouveaux utilisateurs generes par utilisateur existant\n5. **Optimisez** — Si K > 1, votre croissance est virale",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le module **Referral Network** vous permet de creer et gerer votre programme de parrainage. Definissez les recompenses, suivez les ambassadeurs et detectez la fraude automatiquement.",
      },
    ],
    linkedModule: {
      label: 'Referral Network',
      path: '/growth/referral-dashboard',
      icon: 'UsersRound',
    },
  },
  {
    slug: 'experimentation',
    title: 'Tester, mesurer, iterer',
    duration: '3 min',
    glossaryTerms: ['roi', 'kpi'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "L'experimentation est au coeur du growth marketing. Chaque hypothese doit etre testee, mesuree et validee (ou invalidee) avant d'etre deployee a grande echelle.\n\nLe cycle d'experimentation :\n- **Hypothese** — \"Si nous ajoutons un CTA dans l'email de bienvenue, le taux d'activation augmentera de 10%\"\n- **Test** — A/B test avec et sans CTA\n- **Mesure** — Comparaison des taux d'activation\n- **Decision** — Garder ou abandonner selon les resultats",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Sans experimentation structuree, vous prenez des decisions basees sur l'intuition plutot que sur les donnees. Les meilleures equipes growth lancent 10 a 20 experiences par semaine. Meme si 80% echouent, les 20% qui reussissent generent une croissance significative.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Collectez des idees** — Brainstorming avec l'equipe, feedbacks clients, analyse concurrence\n2. **Priorisez avec ICE** — Impact (1-10) x Confiance (1-10) x Facilite (1-10)\n3. **Definissez la metrique** — Quel KPI va bouger si l'hypothese est vraie ?\n4. **Lancez le test** — Duree minimale : 1 semaine ou 100 conversions\n5. **Analysez et documentez** — Meme les echecs apportent des apprentissages",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Utilisez la **Content Factory** pour generer rapidement du contenu a tester (emails, posts LinkedIn), et le **Marketing Dashboard** pour suivre l'impact de chaque experience sur vos KPIs.",
      },
    ],
    linkedModule: {
      label: 'Content Factory',
      path: '/growth/content-factory',
      icon: 'PenTool',
    },
  },
  {
    slug: 'your-growth-dashboard',
    title: 'Votre Marketing Dashboard dans FO',
    duration: '3 min',
    glossaryTerms: ['marketing-dashboard', 'kpi', 'pipeline'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Le Marketing Dashboard est votre centre de commande growth. Il regroupe en un seul ecran les metriques cles de vos efforts marketing : contenus generes, prospects dans le pipeline, taux de conversion du parrainage et revenu estime.",
      },
      {
        type: 'methodology',
        title: 'Comment l\'utiliser ?',
        content:
          "1. **Consultez le Dashboard quotidiennement** — 5 minutes chaque matin pour voir les tendances\n2. **Suivez les 4 KPIs principaux** — Contenus generes, prospects contactes, taux de referral, MRR estime\n3. **Identifiez les anomalies** — Un KPI en baisse ? Creusez pour comprendre pourquoi\n4. **Agissez vite** — Utilisez les modules lies (Content Factory, Sales Intelligence) pour rectifier\n5. **Mesurez l'impact** — Revenez le lendemain verifier que vos actions portent leurs fruits",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Rendez-vous sur **Marketing Dashboard** pour voir vos metriques en temps reel. Cliquez sur chaque KPI pour acceder au module correspondant et agir directement.",
      },
    ],
    linkedModule: {
      label: 'Marketing Dashboard',
      path: '/growth/marketing-dashboard',
      icon: 'TrendingUp',
    },
  },
];
